// Copyright: IQGeo Limited 2010-2019
import _ from 'underscore';
import myw from 'myWorld-base';
import L from 'leaflet';
import geometry from 'myWorld/base/geometry';
import 'L.Editable';
import 'L.Path.Drag';
import './mapControl';
import './coordinatesDialog';

myw.GeomDrawMode = myw.MapInteractionMode.extend('GeomDrawMode', {
    // Default options, create, edit
    options: {
        editableOptions: {
            lineGuideOptions: {
                color: 'red'
            }
            //ENH: set an a opacitiy on vertices
            // vertexMarkerClass: L.Editable.VertexMarker.extend({options: {opacity: 0.75}})
        },
        create: {
            polygon: {
                color: 'red'
            },
            polyline: {
                color: 'red'
            },
            marker: {
                icon: new L.DivIcon({
                    iconSize: new L.Point(8, 8),
                    iconAnchor: new L.Point(8, 8),
                    className: 'leaflet-div-icon edit-marker-selected'
                })
            }
        },
        errorColor: '#e1e100',
        precision: 16 //larger than JS and postGIS precision so we don't to lose any precision unnecessarily (required to support modules that rely on exact spatial coincidence)
    },

    /**
     * Initialization. Doesn't enable the mode
     * @class  Geometry Draw interaction mode.
     *         Used in myWorld's feature Editing and measureTool functionalities
     * @param  {L.MapControl} map     Map on which the mode will be enabled
     * @param  {Object} [options] *optional* for overwriting default options
     *                                see {@link https://github.com/Leaflet/Leaflet.draw#options for full documentation}
     * @constructs
     * @extends {MapInteractionMode}
     */
    initialize(map, options) {
        this.app = myw.app;
        this.map = map;
        this.geomRotateMode = undefined;
        this._creating = undefined;
        this._clicked = undefined;
        this.currentFeature = undefined;

        if (options) {
            _.extend(this.options.create, options.create);
            _.extend(this.options.edit, options.edit);
        }

        _.bindAll(
            this,
            '_onKeyDown',
            '_validatePolygon',
            'onVertexDragStart',
            'onVertexDragEnd',
            'onMarkerDragStart',
            'onMarkerDragEnd',
            'getRotation',
            'setRotation',
            '_changed'
        );
    },

    /**
     * Sets the geometry type to create/edit.
     * @param {string} geomType One of 'Point', 'LineString' or 'Polygon'
     */
    setGeomType(geomType) {
        this._geomType = geomType;
    },
    /**
     * Sets the overlay to use when editing or whether to create a new geometry
     * @param {null|L.Marker|L.Polyline|L.Polygon} overlay Overlay/geometry to edit, null means create a new geometry
     */
    setEditOverlay(overlay, rotatable = false, rotation = 0) {
        this._overlay = overlay;
        this._isOverlayRotatable = rotatable;
        this._rotation = rotation;
        if (!overlay) return;

        const geom = myw.geometry(overlay.toGeoJSON(this.options.precision).geometry);
        this._initialGeom = geom;

        if (this._geomType !== 'Point') {
            geom.removeDuplicates();
            const cleanOverlay = L.GeoJSON.geometryToLayer(geom);
            overlay.setLatLngs(cleanOverlay.getLatLngs());
        }
    },

    /**
     * Enables geometry editing mode with the currently set options
     */
    enable() {
        // since only one MapInteractionMode can be enabled at a time, clear existing geomRotateMode reference
        this.geomRotateMode = undefined;

        // add keyboard shortcuts : Escape and backspace
        this.map.getDivElement().on('keydown', this._onKeyDown);
        this.currentFeature = this.app.currentFeature;

        this._redoStack = new myw.Util.RedoStack();

        geometry.init();

        this.map.editTools = new L.Editable(this.map, this.options.editableOptions);

        // We only want to define _creating/_clicked the first time we enable geomDrawMode
        if (this._creating === undefined) {
            this._creating = !this._overlay;
            this._clicked = !!this._overlay; //when editing a geometry we always have at least a point
        }

        let success;

        this._moveOverlayToPane('edit');

        if (!this._overlay) {
            this._appendingVertex = true; //True when starting to draw linestring
            success = this._createGeom(this._geomType);
        } else {
            this._appendingVertex = false; //False when editing linestring
            success = this._editGeom(this._overlay, this._geomType);
            this._redoStack.push(this.getLatLngs());
            if (this.geomType == 'LineString') this.addSelectedToLastMarker();
        }
        if (!success) return false;

        //set context menu items
        this._setContextMenuForGeomDrawMode();

        // when the feature is drawn remove the delete last and reverse functionality from context menu
        this.map.on('editable:drawing:commit', () => {
            if (this.geomType == 'LineString') this.toggleAppendingVertex();
        });

        this.map.on('editable:vertex:dragstart', this.onVertexDragStart);
        this.map.on('editable:vertex:dragend', this.onVertexDragEnd);
        this.map.on('editable:dragstart', this.onMarkerDragStart);
        this.map.on('editable:dragend', this.onMarkerDragEnd);
        this.map.on(
            'editable:dragend editable:drawing:clicked editable:vertex:deleted editable:vertex:dragend',
            this._changed
        );

        if (this._geomType == 'Polygon') {
            this.isPolygonValid = true;
            //enable holes in polygons
            this._overlay.on('click', function(e) {
                if ((e.originalEvent.ctrlKey || e.originalEvent.metaKey) && this.editEnabled()) {
                    this.editor.newHole(e.latlng);
                }
            });

            this.map.on('editable:drawing:clicked', this._validatePolygon);
            this.map.on('editable:vertex:dragend', this._validatePolygon);
        }
        return true;
    },

    /**
     * Disables geometry editing mode
     */
    disable() {
        this.map.getDivElement().off('keydown', this._onKeyDown);
        this.map.contextmenu.removeAllItems();
        if (!this._overlay) return;

        // When moving to geomRotateMode we are not finished and do not want to teardown this geomDrawMode.
        const finished = !this.isRotating();
        if (finished && !this._creating) this.cancel();

        this._overlay.disableEdit();
        if (finished) {
            if (this._creating) this.map.removeLayer(this._overlay);

            this._overlay = null;
            this._initialOverlay = null;
        }

        //Remove place vertex dialog when leaving the mode
        if (this.coordinatesDialog) {
            this.coordinatesDialog.destroy();
        }

        this.map.off('draw:created');
        this.map.off('editable:vertex:dragstart', this.onVertexDragStart);
        this.map.off('editable:vertex:dragend', this.onVertexDragEnd);
        this.map.off('editable:dragstart', this.onMarkerDragStart);
        this.map.off('editable:dragend', this.onMarkerDragEnd);
        this.map.off(
            'editable:dragend editable:drawing:clicked editable:vertex:deleted editable:vertex:dragend',
            this._changed
        );
    },

    handleMapClick(ev) {
        if (
            this.app.editMode &&
            (!this._creating && !this._appendingVertex) &&
            !this._vertexDragging &&
            !this.markerDragging
        ) {
            //app is in edit mode and we're not creating a new feature so we want for
            //a click to select another feature
            //returning false, makes the next mode on the stack handle the click
            return false;
        }
    },

    onVertexDragStart() {
        this._vertexDragging = true;
    },

    onVertexDragEnd() {
        //hack to workaround spurious click events at the end of a vertex drag
        setTimeout(() => (this._vertexDragging = false), 600);
    },

    onMarkerDragStart() {
        this.markerDragging = true;
    },
    onMarkerDragEnd() {
        //hack to workaround spurious click events at the end of a marker drag
        setTimeout(() => (this.markerDragging = false), 600);
    },

    /**
     * Revert changes to the geometry being edited
     */
    cancel() {
        if (!this._initialGeom) return;
        const initialOverlay = L.GeoJSON.geometryToLayer(this._initialGeom);
        if (this._geomType == 'Point') {
            if (this._initialOverlay) {
                // remove the overlay which was created after clearing the geometry in clear method (see below)
                this.map.removeLayer(this._overlay);
            } else {
                this._overlay.setLatLng(initialOverlay.getLatLng());
            }
        } else {
            //When we clear the geometry in ui we store the initial overlay that has all original styles
            //The overlay is linked to a feature respresentation, so we need to keep the initial overlay
            //otherwise we loose the highlight
            if (this._initialOverlay) {
                this.map.addLayer(this._initialOverlay.editor.feature);
                this._overlay.disableEdit();
                this.map.removeLayer(this._overlay);
                this._overlay = this._initialOverlay;
            }
            this._overlay.setLatLngs(initialOverlay.getLatLngs());
            if (this._geomType == 'Polygon') this._markPolygonAsValid();
        }
    },

    _setContextMenuForGeomDrawMode(allowReversal = true) {
        const geomType = this._geomType;
        const contextmenu = this.map.contextmenu;
        contextmenu.removeAllItems();

        const undo = {
            text: myw.msg('GeomDrawMode', 'undo'),
            icon: 'images/inactiveContextMenuItem.png',
            callback: this.undo.bind(this)
        };
        const clear = {
            text: myw.msg('GeomDrawMode', 'clear'),
            icon: 'images/inactiveContextMenuItem.png',
            callback: this.clear.bind(this)
        };
        const deleteLast = {
            text: myw.msg('GeomDrawMode', 'delete_last'),
            icon: 'images/inactiveContextMenuItem.png',
            callback: this.deleteLast.bind(this)
        };
        const reverse = {
            text: myw.msg('GeomDrawMode', 'reverse'),
            icon: 'images/inactiveContextMenuItem.png',
            callback: this.reverse.bind(this)
        };
        const appendVertex = {
            text: myw.msg('GeomDrawMode', 'append_vertex'),
            icon: 'images/inactiveContextMenuItem.png',
            disabled: !!this._appendingVertex,
            callback: this.toggleAppendingVertex.bind(this)
        };
        const coordinates = {
            text: myw.msg('GeomDrawMode', 'place_vertex'),
            icon: 'images/inactiveContextMenuItem.png',
            callback: this.toggleCoordinatesDialog.bind(this),
            disabled: !this._clicked
        };
        const activateGeomRotateMode = {
            text: myw.msg('GeomRotateMode', 'geomRotateMode'),
            icon: 'images/inactiveContextMenuItem.png',
            callback: this.activateGeomRotateMode.bind(this)
        };

        contextmenu.addItem(coordinates);
        // Geometry specific menuitems
        if (allowReversal && (geomType == 'Linestring' || geomType == 'LineString')) {
            contextmenu.addItem(deleteLast);
            contextmenu.addItem(reverse);
            contextmenu.addItem(appendVertex);
        } else if (allowReversal && geomType == 'Polygon') {
            contextmenu.addItem(deleteLast);
        }
        if (this._isOverlayRotatable) {
            contextmenu.addItem(activateGeomRotateMode);
        }
        if (
            //If context menu requires seperator
            geomType == 'Linestring' ||
            geomType == 'LineString' ||
            geomType == 'Polygon' ||
            this._isOverlayRotatable
        ) {
            contextmenu.addItem('-');
        }

        // Common menuitems
        if (allowReversal) contextmenu.addItem(undo);
        contextmenu.addItem(clear);

        const action = this._parseAction('snapping.toggle', false); //Don't show error in console if plugin is not available
        if (this._isActionAvailable(action)) {
            //Add snapping if available
            const idx = contextmenu._items.length;
            const item = this._getOptionsForAction(action, idx);
            contextmenu.addItem(item);
        }
    },

    /**
     * Initalize geometry creation mode mode
     * @param {string}  geomType        One of 'Point', 'LineString' or 'Polygon'
     * @private
     */
    _createGeom(geomType) {
        const editTools = this.map.editTools;

        this.geomType = geomType;

        if (geomType == 'Polygon') {
            this._overlay = editTools.startPolygon(null, this.options.create.polygon);
        } else if (geomType == 'Linestring' || geomType == 'LineString') {
            this._overlay = editTools.startPolyline(null, this.options.create.polyline);
        } else if (geomType == 'Point') {
            this._overlay = editTools.startMarker(null, this.options.create.marker);
        }

        //listen for "click" events so we can return the coordinates of a geometry while it's
        //being created (instead of only after it goes into 'edit' mode)
        this.map.on(
            'editable:drawing:clicked',
            function(e) {
                this._clicked = true;
                //Refresh context menu to enable coordinates dialog
                this._setContextMenuForGeomDrawMode(this.geomType == 'LineString' ? true : false);
            },
            this
        );

        return true;
    },

    /*
     * If the latest point intersects with the previously laid points,
     * Its considered as an invalid polygon and the point is removed from the map
     */
    _validatePolygon() {
        if (this.geomType !== 'Polygon') return;
        geometry.init().then(() => {
            this.isPolygonValid = true;

            if (this._overlay) {
                const pol = geometry(this._overlay.toGeoJSON(this.options.precision).geometry);
                this.isPolygonValid = pol.isValid();
            }

            if (this.isPolygonValid) {
                this._markPolygonAsValid();
            } else {
                this._markPolygonAsInvalid();
            }
        });
    },

    /*
     * Marks the polygon being drawn or edited as invalid by changing its color to the errorColor
     */
    _markPolygonAsInvalid() {
        this._setPolygonColor(this.options.errorColor);
    },

    /*
     * Marks the polygon being drawn or edited as valid by changing its color to its configured color
     */
    _markPolygonAsValid() {
        if (this._overlay) this._setPolygonColor(this.options.create.polygon.color);
    },

    /*
     * Sets the color provided as the polygon's boundary and fill color
     * @param {string} color
     */
    _setPolygonColor(color) {
        this._overlay.setStyle({ color: color, fillColor: color });
    },

    /**
     * Initalize geometry editing mode
     * @param {ILayer} overlay      Overlay for starting an edit session for a newly created geometry
     * @param {string} geomType     One of 'Point', 'LineString' or 'Polygon'
     * @private
     */
    _editGeom(overlay, geomType) {
        this.geomType = geomType;

        if (
            geomType == 'MultiLineString' ||
            geomType == 'MultiPolygon' ||
            geomType == 'MultiPoint'
        ) {
            const msg = 'No support for Multi* geometry types';
            console.log(`GeomDrawMode: ${msg}`);
            return msg;
        }
        overlay.enableEdit(this.map);

        if (geomType != 'Point') {
            // This makes the polygon (usually the highlight version of the feature's geometry)
            // we are editing draw over another polygon (usually the normal version of the feature's geometry).
            // This is important visually and for event handling.
            // When we first start editing a feature, this order is ok however clicking save and then editing again, the normal geometry
            // is on top and will capture mouse events, which we dont' want.
            overlay._path.parentNode.appendChild(overlay._path);
        }

        return true;
    },

    /**
     * Obtains the coordinates of the edited geometry. (geojson format for geometry coordinates)
     * @return {Array} depending on geom type: Point: [102.0, 0.5], LineString:  [ [102.0, 0.0], [103.0, 1.0], [104.0, 0.0], [105.0, 1.0]]
     *                           or Polygon: [ [ [100.0, 0.0], [101.0, 0.0], [101.0, 1.0],[100.0, 1.0], [100.0, 0.0] ] ]
     * @private
     */
    _getEditedGeomCoords() {
        if (this.geomType == 'Point' && !this._clicked) return null; //marker mode returns a coordinate even when the user hasn't clicked

        const coords = this._overlay.toGeoJSON(this.options.precision).geometry.coordinates;

        return coords;
    },

    _onKeyDown(e) {
        if (e.keyCode == 27) {
            this.clear();
        } else if (e.keyCode == 8 && this._geomType != 'Point') {
            this.deleteLast();
        } else if (e.keyCode == 90 && e.ctrlKey) {
            //Ctrl+z
            this.undo();
        }
    },

    /**
     * Returns the geometry drawn by the user.
     * @return {geojsonGeom}
     */
    getGeometry() {
        if (!this._overlay.editEnabled()) return null;

        const geom = {
            type: this.geomType == 'Linestring' ? 'LineString' : this.geomType,
            coordinates: this._getEditedGeomCoords(),
            world_name: this.map.worldId
        };
        return geom;
    },

    getRotation() {
        return this._rotation;
    },

    setRotation(theta) {
        // ENH: Add validation before updating rotation
        this._rotation = theta;
    },

    // remove the geometry from the map
    clear() {
        if (this._overlay) {
            if (!this.initialOverlay) this._initialOverlay = this._overlay;
            this.map.removeLayer(this._overlay);
            this._createGeom(this._geomType);
            this._setContextMenuForGeomDrawMode();
        }
    },

    // reverse the geometry
    reverse() {
        const latLngs = this._overlay && this._overlay.getLatLngs();
        latLngs.reverse();
        if (!this._appendingVertex) {
            this.addSelectedToLastMarker();
        }
        this.map.editTools.anchorForwardLineGuide(latLngs[latLngs.length - 1]);
        this._changed();
    },

    // Create new geometry of line from last drawn point
    toggleAppendingVertex() {
        if (!this._overlay || !this._overlay.getLatLngs) return;
        this._appendingVertex = !this._appendingVertex;
        this._overlay.editor.continueForward();
        this._setContextMenuForGeomDrawMode();
        if (!this._appendingVertex) {
            this.addSelectedToLastMarker();
        } else {
            //Dont add marker when adding new point - is obvious from construction line
            this.removeSelectedMarker();
        }
    },

    toggleCoordinatesDialog() {
        if (!this.coordinatesDialog) {
            this.coordinatesDialog = new myw.CoordinatesDialog(this, {
                owner: this,
                latLngs: this.getLatLngs()
            });
        }
        this.coordinatesDialog.show();
    },

    refreshDialog(latLngs) {
        this.coordinatesDialog.render(latLngs);
    },

    // Add marker denoting the last vertex drawn to vertex
    addSelectedToLastMarker() {
        if (!this._overlay || !this._overlay.getLatLngs) return;
        const latLngs = this._overlay.getLatLngs();
        if (latLngs.length == 0) return;
        this.removeSelectedMarker();
        const newIcon = latLngs[latLngs.length - 1].__vertex._icon;
        newIcon.classList.add('leaflet-last-marker');
    },

    // Remove direction marker class from all markers in geom
    removeSelectedMarker() {
        if (!this._overlay || !this._overlay.getLatLngs) return;
        const latLngs = this._overlay.getLatLngs();
        latLngs.forEach(latLng => {
            if (latLng.__vertex._icon.className.includes('leaflet-last-marker')) {
                latLng.__vertex._icon.classList.remove('leaflet-last-marker');
            }
        });
    },

    // delete the last vertex
    deleteLast() {
        const initialLatLngs = this._overlay.getLatLngs();
        const latLngCount =
            this.geomType == 'Polygon' ? initialLatLngs[0].length : initialLatLngs.length;
        const latLngs = this.geomType == 'Polygon' ? initialLatLngs[0] : initialLatLngs;

        if (latLngCount > 1) {
            this._overlay.editor.removeLatLng(latLngs[latLngs.length - 1]);
            this._overlay.editor.setDrawnLatLngs(latLngs);
            this.map.editTools.anchorForwardLineGuide(latLngs[latLngs.length - 1]);
            this._overlay.editor.refresh();
        } else {
            this.clear();
        }
        //Add selected marker back in when not in appending mode
        if (!this._appendingVertex && this.geomType == 'LineString') {
            this.addSelectedToLastMarker();
        }
    },

    undo() {
        const latLngs = this._redoStack.unDo();
        myw.trace(
            'geomDrawMode',
            3,
            `Undo. stack: ${this._redoStack.stack.length}, curIndex: ${
                this._redoStack.currentStackIndex
            }, latLngs: ${!!latLngs}`
        );
        if (latLngs) this.setLatLngs(latLngs);
        if (this.geomType == 'LineString') {
            this.addSelectedToLastMarker(); //Add marker back in (will never be in appendingVertex Mode)
        }
        if (this.coordinatesDialog) {
            this.refreshDialog(latLngs);
        }
    },

    canBeRotated() {
        if (!this._isOverlayRotatable) return false;
        if (this._creating) return this._clicked;
        return true;
    },

    /**
     * Indicates if we have an activated geomRotateMode
     * @return {boolean} True if we have a geomRotateMode reference
     * @private
     */
    isRotating() {
        return this.geomRotateMode;
    },

    activateGeomRotateMode() {
        let geomRotateMode;
        const options = { rotation: this.getRotation() };

        if (!this.canBeRotated()) return;
        if (this.coordinatesDialog) return;

        geomRotateMode = new myw.GeomRotateMode(this.map, options);
        geomRotateMode.setGeomType(this._geomType);
        geomRotateMode.setEditOverlay(this._overlay);
        geomRotateMode.setRotationChangeHandler(this.setRotation);
        this.geomRotateMode = geomRotateMode;

        this.map.setInteractionMode(this.geomRotateMode);
    },

    endGeomRotateMode() {
        if (this.isRotating() && this.map.currentInteractionMode() instanceof myw.GeomRotateMode) {
            this.map.endCurrentInteractionMode();
            this.geomRotateMode = undefined;
        }
    },

    /**
     * Returns a copy of the current clat/long coordinates of the overlay being created/edited
     */
    getLatLngs() {
        if (this._overlay.getLatLng) return cloneLatLng(this._overlay.getLatLng());
        return cloneLatLngs(this._overlay.getLatLngs());
    },

    /**
     * Set the overlay being edited to a given list of latlngs
     * @param {L.LatLng[]} latLngs
     */
    setLatLngs(latLngs) {
        if (!latLngs) return;
        if (!this.initialOverlay) this._initialOverlay = this._overlay;
        //disable editing before changing overlay otherwise vertex handles are not updated accordingly
        this._overlay.disableEdit();

        if (this._overlay.setLatLng) this._overlay.setLatLng(cloneLatLng(latLngs));
        else this._overlay.setLatLngs(cloneLatLngs(latLngs));

        this._overlay.enableEdit();
        if (this._creating) this._overlay.editor.startDrawingForward();
        this._setContextMenuForGeomDrawMode();
    },

    //called when the user changes the geometry
    //information can then be used for undo
    _changed() {
        const latLngs = this.getLatLngs();
        if (!_.isEqual(this._redoStack.current, latLngs)) {
            this._redoStack.push(latLngs);
            myw.trace(
                'geomDrawMode',
                6,
                `geometry changed. stack size: ${this._redoStack.currentStackIndex}`
            );
        }
        if (this.coordinatesDialog) {
            this.refreshDialog(latLngs);
        }
    },

    //move current overlay to a given pane
    _moveOverlayToPane(pane, parent) {
        const overlayParent = (this._overlay && this._overlay.myw_parent) || parent;
        if (overlayParent) {
            //move to edit pane so it is always above svgs (which holds vector layer feature reps)
            overlayParent.removeLayer(this._overlay);
            this._overlay.options.pane = pane;
            overlayParent.addLayer(this._overlay);
        }
    }
});

function cloneLatLngs(latLngs) {
    if (latLngs.lat) return cloneLatLng(latLngs);
    //now assume its an array
    if (!latLngs.length) return [];
    if (latLngs[0].lat) return latLngs.map(cloneLatLng);
    //array of arrays of latlngs
    return latLngs.map(cloneLatLngs);
}
function cloneLatLng(latLng) {
    return L.latLng(latLng.lat, latLng.lng);
}

export default myw.GeomDrawMode;
